import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
    Alert, Avatar, Box, Button, Chip, CircularProgress, Collapse, IconButton, Paper, Table,
    TableBody, TableCell, TableContainer, TableFooter, TableHead, TableRow, Typography,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import SeasonBreadcrumb from '../../../components/admin/SeasonBreadcrumb';
import StatField from '../../../components/admin/StatField';
import { useSeason } from '../../../context/SeasonContext';
import {
    COMPACT_STAT_FIELDS, REVIEW_STATUS, STAT_FIELDS, breakdownRows, fetchMatch,
    fetchMatchPlayerStats, fetchRound, fetchScoringRules, setMatchReviewStatus,
    setPlayerStatCorrection, syncMatchStats, translatePosition,
} from '../../../services/calendar';
import { formatKickoff, formatMoment } from '../../../utils/formatDate';
import { avatarSizes, containers, page } from '../../../theme/commonStyles';
import { useNotification } from '../../../context/NotificationContext';

const FILTERS = {
    ALL: 'all',
    WITH_POINTS: 'points',
    CORRECTED: 'corrected',
};

/**
 * Gewonnen, gelijk of verloren voor de club van deze speler. Afgeleid uit de
 * eindstand, dus alleen om te laten zien — corrigeren doe je aan de eindstand.
 */
function matchResultFor(player, match) {
    if (typeof match.homeGoals !== 'number' || typeof match.awayGoals !== 'number') return 'nog geen eindstand';
    const own = player.isHome ? match.homeGoals : match.awayGoals;
    const other = player.isHome ? match.awayGoals : match.homeGoals;
    if (own > other) return 'gewonnen';
    if (own < other) return 'verloren';
    return 'gelijkgespeeld';
}

/**
 * De puntenopbouw van één speler, per scoringsregel in de volgorde van de
 * puntentelling. Hetzelfde overzicht als de oude modal, nu naast de cijfers waar
 * het over gaat.
 */
function Breakdown({ rows, points }) {
    if (rows.length === 0) {
        return (
            <Typography variant="body2" color="text.secondary">
                Nog geen punten berekend. Haal de wedstrijd op om de puntenopbouw te zien.
            </Typography>
        );
    }

    return (
        <Table size="small">
            <TableHead>
                <TableRow>
                    <TableCell>Actie</TableCell>
                    <TableCell align="right">Aantal</TableCell>
                    <TableCell align="right">Punten</TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {rows.map((row) => (
                    <TableRow key={row.rule}>
                        <TableCell>{row.action}</TableCell>
                        <TableCell align="right">{row.count}</TableCell>
                        <TableCell align="right" sx={{ color: row.points < 0 ? 'error.main' : 'inherit' }}>
                            {row.points}
                        </TableCell>
                    </TableRow>
                ))}
            </TableBody>
            <TableFooter>
                <TableRow>
                    <TableCell colSpan={2} sx={{ fontWeight: 700 }}>Totaal</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, color: 'primary.main' }}>{points}</TableCell>
                </TableRow>
            </TableFooter>
        </Table>
    );
}

/**
 * Wedstrijdcontrole: het werkscherm. Elk getal is een invoerveld, dus typen is
 * corrigeren. Een gecorrigeerd veld is van de administrator: de sync laat het met
 * rust en de punten herrekenen meteen.
 *
 * Het afvinken van de wedstrijd ("Akkoord & vergrendelen") is het slotstuk — daarna
 * raakt de import de hele wedstrijd niet meer aan.
 */
function AdminWedstrijd() {
    const { seasonId, roundKey, fixtureId } = useParams();
    const { isLocked } = useSeason();
    const notify = useNotification();

    const [round, setRound] = useState(null);
    const [match, setMatch] = useState(null);
    const [players, setPlayers] = useState([]);
    const [scoringRules, setScoringRules] = useState({});
    const [loading, setLoading] = useState(true);
    const [syncing, setSyncing] = useState(false);
    const [savingField, setSavingField] = useState(null);
    const [expanded, setExpanded] = useState(() => new Set());
    const [filter, setFilter] = useState(FILTERS.ALL);
    const [error, setError] = useState('');

    const load = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const [roundData, matchData, rules] = await Promise.all([
                fetchRound(seasonId, roundKey),
                fetchMatch(seasonId, roundKey, fixtureId),
                fetchScoringRules(),
            ]);
            setRound(roundData);
            setMatch(matchData);
            setScoringRules(rules);

            if (roundData && matchData) {
                setPlayers(await fetchMatchPlayerStats(seasonId, roundKey, roundData, fixtureId));
            }
        } catch (err) {
            console.error('Kon de wedstrijd niet ophalen:', err);
            setError('De wedstrijd kon niet opgehaald worden.');
        } finally {
            setLoading(false);
        }
    }, [seasonId, roundKey, fixtureId]);

    useEffect(() => { load(); }, [load]);

    /** Herlaadt alleen de spelers en de punten; gebruikt na een correctie. */
    const reloadPlayers = useCallback(async () => {
        if (!round) return;
        const [matchData, playerData] = await Promise.all([
            fetchMatch(seasonId, roundKey, fixtureId),
            fetchMatchPlayerStats(seasonId, roundKey, round, fixtureId),
        ]);
        setMatch(matchData);
        setPlayers(playerData);
    }, [round, seasonId, roundKey, fixtureId]);

    const approved = match?.reviewStatus === REVIEW_STATUS.APPROVED;
    const readOnly = approved || isLocked;

    const handleCorrection = async (playerId, field, value) => {
        setSavingField(`${playerId}:${field}`);
        setError('');
        try {
            await setPlayerStatCorrection({
                seasonId, roundKey, fixtureId, playerId, field, value,
            });
            await reloadPlayers();
        } catch (err) {
            console.error('Correctie mislukt:', err);
            setError(err.message || 'De correctie kon niet opgeslagen worden.');
        } finally {
            setSavingField(null);
        }
    };

    const handleSync = async () => {
        setSyncing(true);
        setError('');
        try {
            const result = await syncMatchStats(seasonId, roundKey, fixtureId);
            notify.success(result.message);
            await load();
        } catch (err) {
            console.error('Ophalen mislukt:', err);
            setError(err.message || 'De statistieken konden niet opgehaald worden.');
        } finally {
            setSyncing(false);
        }
    };

    const handleReview = async () => {
        setError('');
        try {
            await setMatchReviewStatus(seasonId, roundKey, fixtureId, !approved);
            notify.success(approved
                ? 'Wedstrijd heropend — de sync mag deze cijfers weer bijwerken.'
                : 'Wedstrijd vergrendeld — de sync raakt deze cijfers niet meer aan.');
            await load();
        } catch (err) {
            console.error('Status wijzigen mislukt:', err);
            setError(err.message || 'De status kon niet gewijzigd worden.');
        }
    };

    const counts = useMemo(() => ({
        [FILTERS.ALL]: players.length,
        [FILTERS.WITH_POINTS]: players.filter((p) => (p.points || 0) !== 0).length,
        [FILTERS.CORRECTED]: players.filter((p) => Object.keys(p.manual).length > 0).length,
    }), [players]);

    const visiblePlayers = useMemo(() => players.filter((player) => {
        if (filter === FILTERS.WITH_POINTS) return (player.points || 0) !== 0;
        if (filter === FILTERS.CORRECTED) return Object.keys(player.manual).length > 0;
        return true;
    }), [players, filter]);

    const toggle = (playerId) => setExpanded((current) => {
        const next = new Set(current);
        if (next.has(playerId)) next.delete(playerId);
        else next.add(playerId);
        return next;
    });

    if (loading) {
        return <Box sx={page.loadingContainer}><CircularProgress /></Box>;
    }
    if (!match || !round) {
        return (
            <Box>
                <SeasonBreadcrumb trail={[{ label: 'Kalender & rondes', to: `/admin/season/${seasonId}/kalender` }]} />
                <Alert severity="error">{error || 'Deze wedstrijd bestaat niet in de kalender.'}</Alert>
            </Box>
        );
    }

    const score = typeof match.homeGoals === 'number' && typeof match.awayGoals === 'number'
        ? `${match.homeGoals} – ${match.awayGoals}`
        : '– : –';

    return (
        <Box>
            <SeasonBreadcrumb
                trail={[
                    { label: 'Kalender & rondes', to: `/admin/season/${seasonId}/kalender` },
                    { label: round.title, to: `/admin/season/${seasonId}/kalender/${roundKey}` },
                ]}
                page={match.title}
            />

            <Box sx={{ ...page.header, alignItems: 'flex-end', flexWrap: 'wrap', gap: 2 }}>
                <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                        <Typography variant="h5">{match.homeTeamName}</Typography>
                        <Typography variant="h4" component="div" sx={{ px: 1 }}>{score}</Typography>
                        <Typography variant="h5">{match.awayTeamName}</Typography>
                        {approved && <Chip size="small" color="success" icon={<LockIcon />} label="Akkoord" />}
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        {formatKickoff(match.startingAt)} · laatste sync {formatMoment(match.statsSyncedAt)}
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                    <Button
                        variant="outlined"
                        onClick={handleSync}
                        disabled={syncing || readOnly}
                        startIcon={syncing ? <CircularProgress size={18} color="inherit" /> : null}
                    >
                        {syncing ? 'Bezig…' : 'Opnieuw ophalen'}
                    </Button>
                    <Button
                        variant={approved ? 'outlined' : 'contained'}
                        color="success"
                        onClick={handleReview}
                        disabled={isLocked}
                        startIcon={approved ? <LockOpenIcon /> : <LockIcon />}
                    >
                        {approved ? 'Heropenen' : 'Akkoord & vergrendelen'}
                    </Button>
                </Box>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {approved && (
                <Alert severity="info" sx={{ mb: 2 }}>
                    Deze wedstrijd staat op akkoord: de sync raakt hem niet meer aan en corrigeren kan
                    niet. Heropen hem om iets te wijzigen.
                </Alert>
            )}

            {players.length === 0 ? (
                <Paper sx={{ p: 3 }}>
                    <Typography variant="body1">
                        Er zijn nog geen statistieken voor deze wedstrijd opgehaald.
                        {!readOnly && ' Gebruik "Opnieuw ophalen" om ze bij Sportmonks op te halen.'}
                    </Typography>
                </Paper>
            ) : (
                <>
                    <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                        <Chip
                            label={`Alle spelers ${counts[FILTERS.ALL]}`}
                            onClick={() => setFilter(FILTERS.ALL)}
                            color={filter === FILTERS.ALL ? 'primary' : 'default'}
                            variant={filter === FILTERS.ALL ? 'filled' : 'outlined'}
                        />
                        <Chip
                            label={`Alleen met punten ${counts[FILTERS.WITH_POINTS]}`}
                            onClick={() => setFilter(FILTERS.WITH_POINTS)}
                            color={filter === FILTERS.WITH_POINTS ? 'primary' : 'default'}
                            variant={filter === FILTERS.WITH_POINTS ? 'filled' : 'outlined'}
                        />
                        <Chip
                            label={`Gecorrigeerd ${counts[FILTERS.CORRECTED]}`}
                            onClick={() => setFilter(FILTERS.CORRECTED)}
                            color={filter === FILTERS.CORRECTED ? 'primary' : 'default'}
                            variant={filter === FILTERS.CORRECTED ? 'filled' : 'outlined'}
                        />
                    </Box>

                    <TableContainer component={Paper} sx={containers.glassContainer}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ width: 48 }} />
                                    <TableCell>Speler</TableCell>
                                    {COMPACT_STAT_FIELDS.map((field) => (
                                        <TableCell key={field.key} align="center">{field.short}</TableCell>
                                    ))}
                                    <TableCell align="right">Punten</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {visiblePlayers.map((player) => {
                                    const isOpen = expanded.has(player.playerId);
                                    const corrected = Object.keys(player.manual).length > 0;
                                    const changedPoints = corrected
                                        && player.pointsFromApi !== null
                                        && player.points !== player.pointsFromApi;

                                    return (
                                        <React.Fragment key={player.playerId}>
                                            <TableRow
                                                hover
                                                sx={corrected ? { backgroundColor: 'rgba(254, 203, 0, 0.08)' } : undefined}
                                            >
                                                <TableCell>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => toggle(player.playerId)}
                                                        aria-label={isOpen ? 'Speler dichtklappen' : 'Speler uitklappen'}
                                                    >
                                                        {isOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                                                    </IconButton>
                                                </TableCell>
                                                <TableCell>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                        <Avatar sx={avatarSizes.md}>
                                                            {(player.playerName || '?').charAt(0)}
                                                        </Avatar>
                                                        <Box>
                                                            <Typography variant="body2">{player.playerName}</Typography>
                                                            <Typography variant="caption" color="text.secondary">
                                                                {player.clubName} · {translatePosition(player.position)}
                                                                {!player.effective.isStarter && ' · ingevallen'}
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                </TableCell>
                                                {COMPACT_STAT_FIELDS.map((field) => (
                                                    <TableCell key={field.key} align="center">
                                                        <StatField
                                                            value={player.effective[field.key] || 0}
                                                            apiValue={player.api[field.key]}
                                                            corrected={player.manual[field.key] !== undefined}
                                                            disabled={readOnly}
                                                            saving={savingField === `${player.playerId}:${field.key}`}
                                                            width={62}
                                                            onCommit={(value) => handleCorrection(player.playerId, field.key, value)}
                                                        />
                                                    </TableCell>
                                                ))}
                                                <TableCell align="right">
                                                    <Typography variant="h6" component="div" sx={{ color: 'primary.main' }}>
                                                        {player.points ?? '—'}
                                                    </Typography>
                                                    {changedPoints && (
                                                        <Typography variant="caption" color="text.secondary">
                                                            was {player.pointsFromApi}
                                                        </Typography>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell
                                                    colSpan={COMPACT_STAT_FIELDS.length + 3}
                                                    sx={{ py: 0, borderBottom: isOpen ? undefined : 'none' }}
                                                >
                                                    <Collapse in={isOpen} unmountOnExit>
                                                        <Box sx={{
                                                            display: 'flex',
                                                            gap: 4,
                                                            flexWrap: 'wrap',
                                                            py: 3,
                                                            pl: { xs: 0, md: 6 },
                                                        }}
                                                        >
                                                            <Box sx={{ flex: 1, minWidth: 320 }}>
                                                                <Typography variant="subtitle2" color="text.secondary">
                                                                    Opgehaalde wedstrijddata
                                                                </Typography>
                                                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                                                                    Alles wat Sportmonks voor deze speler levert. Elk veld is te
                                                                    corrigeren; maak het leeg om de API-waarde terug te zetten.
                                                                </Typography>
                                                                <Box sx={{
                                                                    display: 'grid',
                                                                    gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
                                                                    columnGap: 3,
                                                                    rowGap: 1.5,
                                                                }}
                                                                >
                                                                    {STAT_FIELDS.map((field) => (
                                                                        <Box
                                                                            key={field.key}
                                                                            sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}
                                                                        >
                                                                            <Typography
                                                                                variant="body2"
                                                                                sx={{
                                                                                    flex: 1,
                                                                                    color: player.manual[field.key] !== undefined
                                                                                        ? 'primary.main'
                                                                                        : 'text.secondary',
                                                                                }}
                                                                            >
                                                                                {field.label}
                                                                                {field.boolean && ' (0/1)'}
                                                                            </Typography>
                                                                            <StatField
                                                                                value={player.effective[field.key] || 0}
                                                                                apiValue={player.api[field.key]}
                                                                                corrected={player.manual[field.key] !== undefined}
                                                                                disabled={readOnly}
                                                                                saving={savingField === `${player.playerId}:${field.key}`}
                                                                                onCommit={(value) => handleCorrection(player.playerId, field.key, value)}
                                                                            />
                                                                        </Box>
                                                                    ))}
                                                                </Box>
                                                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
                                                                    Resultaat: <strong>{matchResultFor(player, match)}</strong>.
                                                                    Gewonnen, gelijk en verloren zijn niet te corrigeren; die
                                                                    volgen uit de eindstand van de wedstrijd.
                                                                </Typography>
                                                            </Box>
                                                            <Box sx={{ width: { xs: '100%', md: 380 } }}>
                                                                <Typography variant="subtitle2" color="text.secondary">
                                                                    Puntenopbouw
                                                                </Typography>
                                                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                                                                    Per scoringsregel, in de volgorde van de puntentelling.
                                                                </Typography>
                                                                <Breakdown
                                                                    rows={breakdownRows(player.breakdown, scoringRules)}
                                                                    points={player.points ?? 0}
                                                                />
                                                            </Box>
                                                        </Box>
                                                    </Collapse>
                                                </TableCell>
                                            </TableRow>
                                        </React.Fragment>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
                        Klap een speler uit voor alle opgehaalde wedstrijddata en de puntenopbouw. Een
                        gecorrigeerd veld krijgt een gouden rand, de punten herrekenen direct, en de sync
                        laat dat veld voortaan met rust. Alleen spelers met speelminuten staan in de lijst.
                    </Typography>
                </>
            )}
        </Box>
    );
}

export default AdminWedstrijd;
